import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Outlet,
    useLocation,
    useNavigate,
    useResolvedPath,
} from "react-router";
import { Navbar, Typography, Menu, MenuHandler, MenuList, MenuItem, Button, Avatar } from "@material-tailwind/react";
import NavItem from "components/nav-item";
import MaterialIcon from "components/material-icon";
import profilepic from "assets/img/uploadprofilepic.png";
import logo from "./assets/img/logos/gobby-logo-FIN_Stacked-BL-wTag-.png";
import { authSelector, fetchSession } from "./store/auth";
import { AUTH_STATUS } from "./store/auth-status";
import { userSelector, checkUserData, fetchPurchases, fetchSubscription, fetchCredits, fetchOrgStats, canReadPurchases } from "./store/user";
import { setSubscriptionDialogOpen } from "./store/meta";
import { toast } from "react-toastify";
import SubscriptionDialog from "components/dialog/subscription-dialog";
import PendingDowngradeDialog from "components/dialog/pending-downgrade-dialog";
import LoadingScreen from "components/ui/loading-screen";
import { useSessionRefresh } from "./hooks/use-session-refresh";
import useSWR from "swr";
import { fetcherSimple } from "utils/api";
import { STRIPE_PUBLISHABLE_KEY, STRIPE_PORTAL_LINK } from "utils/config";
import { getValidUserLimit, userPictureUrl } from "./utils/consts";

function MyNavLink({ to, icon, children, onNavigate }) {
    let location = useLocation();
    let path = useResolvedPath(to);

    let locationPathname = location.pathname.toLowerCase();
    let toPathname = path.pathname.toLowerCase();

    let isActive =
        locationPathname === toPathname ||
        (locationPathname.startsWith(toPathname) &&
            locationPathname.charAt(toPathname.length) === "/");

    return (
        <button
            onClick={() => onNavigate(to)}
            className="bg-transparent border-none cursor-pointer p-0"
        >
            <NavItem
                ripple={"dark"}
                active={isActive ? "dark" : undefined}
                className="flex items-center gap-1"
            >
                {icon && <MaterialIcon name={icon} size="xl" />}
                {children}
            </NavItem>
        </button>
    );
}

export default function AdminWrapper() {
    const auth = useSelector(authSelector);
    const user = useSelector(userSelector);
    const subscriptionDialogOpen = useSelector((state) => state.meta.subscriptionDialogOpen);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [showPendingDowngradeDialog, setShowPendingDowngradeDialog] = useState(false);
    const userThunksFiredRef = useRef(false);

    const guardedNavigate = useCallback((to) => {
        navigate(to);
    }, [navigate]);

    // Effect: kick off session check when status is unknown.
    // Separated from user-thunk effect to avoid 401s on race.
    useEffect(() => {
        if (auth.status === AUTH_STATUS.UNKNOWN) {
            dispatch(fetchSession());
        }
    }, [auth.status, dispatch]);

    const purchasesFiredRef = useRef(false);

    // Fire user thunks only after session is confirmed. Ref guard prevents
    // re-dispatch on re-renders.
    useEffect(() => {
        if (auth.status === AUTH_STATUS.LOGGED_IN && !userThunksFiredRef.current) {
            userThunksFiredRef.current = true;
            dispatch(checkUserData());
            dispatch(fetchSubscription());
            dispatch(fetchCredits());
            dispatch(fetchOrgStats());
        }
        if (auth.status === AUTH_STATUS.LOGGED_OUT) {
            userThunksFiredRef.current = false;
            purchasesFiredRef.current = false;
        }
    }, [auth.status, dispatch]);

    // Purchases are admin-only data (GET /organisation/purchases needs
    // "Organisation admin"); firing it for members meant a silent 403 on
    // every login. Wait for @me so the permission is known, then fetch
    // only for admins. Member derivations run off subscriptionData.
    useEffect(() => {
        if (auth.status !== AUTH_STATUS.LOGGED_IN || purchasesFiredRef.current) {
            return;
        }
        const userData = user.userData;
        if (!userData) return;
        if (!canReadPurchases(userData)) {
            purchasesFiredRef.current = true;
            return;
        }
        purchasesFiredRef.current = true;
        dispatch(fetchPurchases());
    }, [auth.status, user.userData, dispatch]);

    useEffect(() => {
        if (auth.status === AUTH_STATUS.LOGGED_OUT) {
            toast.warn("Login Session Expired");
            navigate("/login");
        }
    }, [auth.status, navigate]);

    // Consolidated session-refresh (window focus, visibilitychange, online).
    useSessionRefresh(auth.status, dispatch);

    // The seat-reduction workflow belongs to billing admins: with the
    // subscription endpoint readable at the member tier, pending_change
    // fields are no longer an implicit admin signal, so gate on the same
    // predicate the purchases fetches use (main admin or delegated
    // "Organisation admin" holder).
    const pendingSeatChange =
        canReadPurchases(user.userData) &&
        !!user.subscriptionData?.pending_change_time &&
        !!user.subscriptionData?.pending_seat_change;

    const {
        data: usersList,
        mutate: mutateUsers,
    } = useSWR(
        pendingSeatChange ? [`/v2/organisation/users`, "GET"] : null,
        ([path, method]) => fetcherSimple(path, method)
    );

    const {
        data: orgData,
    } = useSWR(
        pendingSeatChange ? [`/v2/organisation`, "GET"] : null,
        ([path, method]) => fetcherSimple(path, method)
    );

    useEffect(() => {
        if (pendingSeatChange && usersList) {
            setShowPendingDowngradeDialog(true);
        }
    }, [pendingSeatChange, usersList]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('openSubscription') === 'true') {
            dispatch(setSubscriptionDialogOpen(true));
            navigate(location.pathname, { replace: true });
        }
    }, [location, dispatch, navigate]);

    const mainNavLinks = [
        { to: "/", icon: "dashboard", label: "Dashboard" },
        { to: "/survey", icon: "forum", label: "Surveys" },
        { to: "/organisation", icon: "group", label: "Organisation" },
    ];

    const userMenuItems = [
        { to: "/account", icon: "account_box", label: "Profile" },
        { to: "/logout", icon: "lock", label: "Logout" },
    ];

    // Versioned through the store so the avatar refreshes after every
    // picture change without a page reload (issue #1128).
    const pfpImageUrl = userPictureUrl("@me", user.pictureVersion);

    // Must come after all hooks (rules of hooks).
    if (
        auth.status === AUTH_STATUS.UNKNOWN ||
        auth.status === AUTH_STATUS.PENDING ||
        auth.status === AUTH_STATUS.LOGGED_OUT ||
        auth.status === AUTH_STATUS.EMAIL_VERIFICATION_REQUIRED
    ) {
        return <LoadingScreen />;
    }

    return (
        <div className="min-w-screen flex flex-col h-screen bg-platinumlight">
            {/* Navbar */}
            <Navbar
                className="fixed top-0 w-full h-[5rem] !bg-black !text-white px-4 py-2 z-40 shadow-md"
                fullWidth
            >
                <div className="flex items-center w-full">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                        <button
                            onClick={() => guardedNavigate("/")}
                            className="bg-transparent border-none cursor-pointer p-0"
                        >
                            <img
                                src={logo}
                                alt="Gobby logo"
                                className="sm:w-28 w-14"
                            />
                        </button>
                    </div>
                    <div className="ml-auto flex items-center gap-4">
                        {/* Desktop menu */}
                        <div className="hidden md:flex gap-4 sm:gap-6 items-center font-display">
                            {mainNavLinks.map(({ to, icon, label }) => (
                                <MyNavLink key={to} to={to} icon={icon} onNavigate={guardedNavigate}>
                                    {label}
                                </MyNavLink>
                            ))}
                        </div>
                        {/* Mobile Hamburger */}
                        <div className="md:hidden">
                            <button
                                onClick={() => setMenuOpen(!menuOpen)}
                                className="text-white focus:outline-none"
                            >
                                <MaterialIcon
                                    name={menuOpen ? "close" : "menu"}
                                    size="2xl"
                                />
                            </button>
                        </div>
                        <Menu>
                            <MenuHandler>
                                <Button
                                    variant="text"
                                    color="blue-gray"
                                    className="flex items-center gap-1 rounded-full py-0.5 pr-2 pl-0.5"
                                >
                                    <Avatar
                                        variant="circular"
                                        size="md"
                                        alt="tania andrew"
                                        className="border border-gray-900 p-0.5"
                                        src={pfpImageUrl}
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null; // prevents looping
                                            currentTarget.src = `${profilepic}`;
                                        }}
                                    />
                                    <MaterialIcon name="keyboard_arrow_down" size="2xl"/>
                                </Button>
                            </MenuHandler>
                            <MenuList className="bg-black text-white border-gray-700 p-4 rounded-xl">
                                {!!user.userData?.is_main_admin && !!STRIPE_PUBLISHABLE_KEY && (
                                    <>
                                        {!user?.isManaged && (
                                            user?.activeSubscription ? (
                                                <MenuItem
                                                    onClick={() => {
                                                        const portalLink = STRIPE_PORTAL_LINK;
                                                        const email = user.userData.email;
                                                        const fullUrl = portalLink + (email ? `?prefilled_email=${encodeURIComponent(email)}` : "");
                                                        window.open(fullUrl, '_blank');
                                                    }}
                                                    className="flex items-center gap-2 hover:bg-white"
                                                >
                                                    <MaterialIcon name="account_balance_wallet" /> Subscription
                                                </MenuItem>
                                            ) : (
                                                <MenuItem onClick={() => dispatch(setSubscriptionDialogOpen(true))} className="flex items-center gap-2 hover:bg-white">
                                                    <MaterialIcon name="credit_card" /> Subscribe
                                                </MenuItem>
                                            )
                                        )}
                                        <MenuItem
                                            onClick={() => guardedNavigate("/top-ups")}
                                            className="flex items-center gap-2 hover:bg-white"
                                        >
                                            <MaterialIcon name="credit_card" /> Top-ups
                                        </MenuItem>
                                    </>
                                )}
                                {userMenuItems.map((item, index) => (
                                    <MenuItem
                                        key={index}
                                        onClick={() => guardedNavigate(item.to)}
                                        className="flex items-center gap-2 hover:bg-white"
                                    >
                                        {item.icon && <MaterialIcon name={item.icon} />}
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </Menu>
                    </div>
                </div>

                {/* Mobile menu */}
                {menuOpen && (
                    <div className="md:hidden mt-2 flex flex-col gap-2 bg-black px-2 py-3 border-t border-gray-700">
                        {mainNavLinks.map(({ to, icon, label }) => (
                            <MyNavLink key={to} to={to} icon={icon} onNavigate={guardedNavigate}>
                                {label}
                            </MyNavLink>
                        ))}
                    </div>
                )}
            </Navbar>

            {/* Page content */}
            <div className="w-full h-screen bg-[#e0e0e0] flex flex-col overflow-auto z-20 pt-[9vh]">
                {/* Page content */}
                <Outlet />

                {/* Footer */}
                <SubscriptionDialog
                    open={subscriptionDialogOpen}
                    onClose={() => dispatch(setSubscriptionDialogOpen(false))}
                />
                {pendingSeatChange && (
                    <PendingDowngradeDialog
                        open={showPendingDowngradeDialog}
                        onClose={() => setShowPendingDowngradeDialog(false)}
                        users={usersList?.filter(u => !u.is_main_admin && u.active) || []}
                        subscription={user.subscriptionData}
                        userLimit={getValidUserLimit(orgData?.user_limit)}  // Add this prop with defensive validation
                        onSuccess={() => {
                            mutateUsers();
                            dispatch(fetchSubscription());
                        }}
                    />
                )}
                <footer className="mt-auto w-full bg-black text-white">
                    <div
                        className="w-full flex flex-row items-center justify-between bg-black"
                    >
                        <div className="grow flex justify-center items-center">
                            <Typography className="font-body text-center text-gray-100 py-4">
                                &copy;{" "}
                                <a
                                    href="https://gobby.io/"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="visited:text-amber hover:text-cta active:text-amber"
                                >
                                    Gobby&nbsp;Limited
                                </a>{" "}
                                2025
                            </Typography>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
}
